import { flattenChildStrategies } from '../bracketDetection.js'

const IDEA_STAGES = new Set(['idea', 'planned', 'setup'])
const ACTIVE_STAGES = new Set(['active'])
const WORKING_STAGES = new Set(['working'])
const CLOSED_STAGES = new Set(['closed', 'reviewed'])

export const LIVE_BROKER_ORDER_STATUSES = new Set([
  'ACCEPTED',
  'AWAITING_CONDITION',
  'AWAITING_ENTRY',
  'AWAITING_PARENT_ORDER',
  'AWAITING_RELEASE_TIME',
  'AWAITING_STOP_CONDITION',
  'NEW',
  'PENDING_ACTIVATION',
  'QUEUED',
  'WORKING',
])

export const TERMINAL_BROKER_ORDER_STATUSES = new Set([
  'CANCELED',
  'CANCELLED',
  'EXPIRED',
  'FILLED',
  'REJECTED',
  'REPLACED',
])

function upper(value) {
  return String(value || '').trim().toUpperCase()
}

function stringValue(value) {
  return value == null ? '' : String(value)
}

function normalizeStage(stage) {
  const normalized = String(stage || 'idea').toLowerCase()
  if (IDEA_STAGES.has(normalized)) return 'idea'
  if (ACTIVE_STAGES.has(normalized)) return 'active'
  if (WORKING_STAGES.has(normalized)) return 'working'
  if (CLOSED_STAGES.has(normalized)) return normalized
  return normalized || 'idea'
}

function orderSymbol(order, fallback = '') {
  return upper(
    order?.symbol
    || order?.instrument?.symbol
    || order?.orderLegCollection?.[0]?.instrument?.symbol
    || fallback
  )
}

function orderAccount(order, fallback = '') {
  return stringValue(
    order?.accountNumber
    || order?.account_number
    || order?.accountId
    || order?.account_id
    || fallback
  )
}

function orderStatus(order, fallback = '') {
  return upper(order?.normalizedStatus || order?.status || fallback)
}

function orderChildren(order) {
  const rawChildren = order?.childOrderStrategies || order?._raw?.childOrderStrategies
  const flattenedRawChildren = flattenChildStrategies(Array.isArray(rawChildren) ? rawChildren : [])
  return [
    ...(Array.isArray(order?.groupedOrders) ? order.groupedOrders : []),
    ...(Array.isArray(order?.childOrders) ? order.childOrders : []),
    ...flattenedRawChildren,
  ]
}

export function isLiveBrokerOrder(order) {
  const status = orderStatus(order)
  if (!status) return false
  if (TERMINAL_BROKER_ORDER_STATUSES.has(status)) return false
  return LIVE_BROKER_ORDER_STATUSES.has(status)
}

export function collectLiveBrokerOrdersForSymbol(orders = [], symbol) {
  const symbolUpper = upper(symbol)
  if (!symbolUpper || !Array.isArray(orders)) return []

  const matches = []
  const visit = (order, inherited = {}, seen = new Set()) => {
    if (!order || seen.has(order)) return
    seen.add(order)

    const nextInherited = {
      symbol: orderSymbol(order, inherited.symbol),
      accountNumber: orderAccount(order, inherited.accountNumber),
      status: orderStatus(order, inherited.status),
    }

    if (
      nextInherited.symbol === symbolUpper
      && isLiveBrokerOrder({ ...order, status: nextInherited.status })
    ) {
      matches.push({ ...order, accountNumber: nextInherited.accountNumber, symbol: nextInherited.symbol })
    }

    for (const child of orderChildren(order)) {
      visit(child, nextInherited, seen)
    }
  }

  for (const order of orders) visit(order)
  return matches
}

export function hasLiveBrokerOrderForSymbol(order, symbol) {
  return collectLiveBrokerOrdersForSymbol([order], symbol).length > 0
}

function positionSymbol(position, fallback = '') {
  return upper(
    position?.symbol
    || position?.instrument?.symbol
    || position?.instrument_symbol
    || position?.security?.symbol
    || fallback
  )
}

function positionAccount(position) {
  return stringValue(
    position?.accountNumber
    || position?.account_number
    || position?.accountId
    || position?.account_id
  )
}

function positionQuantity(position) {
  const candidates = [
    position?.quantity,
    position?.longQuantity,
    position?.shortQuantity,
    position?.shares,
    position?.qty,
    position?.marketValue != null && position?.averagePrice ? Number(position.marketValue) / Number(position.averagePrice) : null,
  ]

  for (const candidate of candidates) {
    const value = Number(candidate)
    if (Number.isFinite(value) && Math.abs(value) > 0) return value
  }
  return 0
}

function tradeAccount(trade) {
  return stringValue(
    trade?.accountNumber
    || trade?.account_number
    || trade?.accountId
    || trade?.account_id
  )
}

function tradeQuantity(trade) {
  const candidates = [trade?.quantity_open, trade?.quantityOpen, trade?.quantity_total, trade?.quantityTotal, trade?.quantity]
  for (const candidate of candidates) {
    const value = Number(candidate)
    if (Number.isFinite(value) && Math.abs(value) > 0) return Math.abs(value)
  }
  return 0
}

function stopAccount(stop) {
  return stringValue(stop?.accountNumber || stop?.account_number || stop?.accountId || stop?.account_id)
}

function stopQuantity(stop) {
  const value = Number(stop?.quantity ?? stop?.qty ?? stop?.remainingQuantity ?? stop?.remaining_quantity)
  return Number.isFinite(value) ? Math.abs(value) : 0
}

function stopPrice(stop) {
  const value = Number(stop?.stopPrice ?? stop?.stop_price ?? stop?.price)
  return Number.isFinite(value) && value > 0 ? value : null
}

export function collectOpenBrokerPositionsForSymbol(positions = [], symbol) {
  const symbolUpper = upper(symbol)
  if (!symbolUpper || !Array.isArray(positions)) return []
  return positions
    .filter((position) => positionSymbol(position) === symbolUpper && Math.abs(positionQuantity(position)) > 0)
    .map((position) => ({
      ...position,
      accountNumber: positionAccount(position),
      symbol: symbolUpper,
      quantity: positionQuantity(position),
    }))
}

export function buildStopLines(stopOrders = []) {
  const byPrice = {}
  for (const stop of Array.isArray(stopOrders) ? stopOrders : []) {
    const price = stopPrice(stop)
    if (price == null) continue
    const key = price.toFixed(2)
    if (!byPrice[key]) byPrice[key] = { price, totalQty: 0, accounts: [] }
    byPrice[key].totalQty += stopQuantity(stop)
    const account = stopAccount(stop)
    if (account) byPrice[key].accounts.push(account.slice(-4))
  }
  return Object.values(byPrice).sort((a, b) => a.price - b.price)
}

export function resolveWorkspaceProtection({
  isActive = false,
  symbol,
  ordersReady = false,
  stopOrders = [],
  brokerPositions = [],
  brokerPositionsReady = false,
  openTrades = [],
} = {}) {
  const symbolUpper = upper(symbol)
  const empty = { totalHeld: 0, totalProtected: 0, unprotected: 0, accountGaps: [] }
  if (!ordersReady || !isActive || !symbolUpper) return empty

  const heldByAccount = {}
  if (brokerPositionsReady) {
    for (const position of collectOpenBrokerPositionsForSymbol(brokerPositions, symbolUpper)) {
      const account = positionAccount(position)
      if (!account) continue
      heldByAccount[account] = (heldByAccount[account] || 0) + Math.abs(Number(position.quantity) || 0)
    }
  } else {
    for (const trade of Array.isArray(openTrades) ? openTrades : []) {
      if (upper(trade?.symbol) !== symbolUpper) continue
      const account = tradeAccount(trade)
      if (!account) continue
      heldByAccount[account] = (heldByAccount[account] || 0) + tradeQuantity(trade)
    }
  }

  const normalizedStops = (Array.isArray(stopOrders) ? stopOrders : [])
    .map(stop => ({ accountNumber: stopAccount(stop), quantity: stopQuantity(stop) }))
    .filter(stop => stop.accountNumber && stop.quantity > 0)

  const totalHeld = Object.values(heldByAccount).reduce((sum, qty) => sum + qty, 0)
  const totalProtected = normalizedStops.reduce((sum, stop) => sum + stop.quantity, 0)
  const accountGaps = Object.entries(heldByAccount)
    .map(([accountNumber, held]) => {
      const protectedQty = normalizedStops
        .filter(stop => stop.accountNumber === accountNumber)
        .reduce((sum, stop) => sum + stop.quantity, 0)
      return {
        accountNumber,
        held,
        protected: protectedQty,
        gap: Math.max(0, held - protectedQty),
      }
    })
    .filter(row => row.gap > 0)

  return {
    totalHeld,
    totalProtected,
    unprotected: Math.max(0, totalHeld - totalProtected),
    accountGaps,
  }
}

function hasSavedSetup(project) {
  if (!project) return false
  return Boolean(
    project.setup_id
    || project.setup_pattern
    || project.setup_trigger
    || project.setup_stop
    || project.setup_target
    || project.entry_price
    || project.stop_loss
    || project.target_price
  )
}

function configuredAccounts(projects = []) {
  const accounts = new Set()
  for (const project of projects) {
    const rows = Array.isArray(project?.account_sizing) ? project.account_sizing : []
    for (const row of rows) {
      const account = stringValue(row?.accountNumber || row?.account_number || row?.accountId || row?.account_id)
      if (account) accounts.add(account)
    }
  }
  return accounts
}

export function resolveWorkspaceState({
  symbol,
  projects = [],
  brokerOrders = null,
  brokerPositions = null,
  brokerOrdersReady = null,
  brokerPositionsReady = null,
  stopOrders = [],
  openTrades = [],
  allowStoredStageDemotion = false,
} = {}) {
  const primary = Array.isArray(projects) ? projects[0] : null
  const storedStage = normalizeStage(primary?.stage)
  const symbolUpper = upper(symbol || primary?.symbol)
  const ordersReady = brokerOrdersReady ?? Array.isArray(brokerOrders)
  const positionsReady = brokerPositionsReady ?? Array.isArray(brokerPositions)
  const openBrokerPositions = collectOpenBrokerPositionsForSymbol(brokerPositions || [], symbolUpper)
  const liveBrokerOrders = collectLiveBrokerOrdersForSymbol(brokerOrders || [], symbolUpper)
  const accounts = configuredAccounts(projects)

  for (const position of openBrokerPositions) {
    if (position.accountNumber) accounts.add(position.accountNumber)
  }
  for (const order of liveBrokerOrders) {
    const account = orderAccount(order)
    if (account) accounts.add(account)
  }

  let effectiveStage = storedStage
  let sourceReason = `stored_${storedStage}`

  if (openBrokerPositions.length > 0) {
    effectiveStage = 'active'
    sourceReason = 'broker_position_open'
  } else if (storedStage === 'active') {
    effectiveStage = 'active'
    sourceReason = ordersReady || positionsReady ? 'stored_active_no_broker_position' : 'stored_active_broker_unknown'
  } else if (liveBrokerOrders.length > 0) {
    effectiveStage = 'working'
    sourceReason = 'broker_order_live'
  } else if (
    allowStoredStageDemotion
    && (ordersReady || positionsReady)
    && hasSavedSetup(primary)
    && !CLOSED_STAGES.has(storedStage)
  ) {
    effectiveStage = 'idea'
    sourceReason = 'saved_setup_no_broker_activity'
  } else if (!ordersReady && !positionsReady) {
    effectiveStage = storedStage
    sourceReason = `stored_${storedStage}_broker_unknown`
  }

  const isClosed = CLOSED_STAGES.has(effectiveStage)
  const liveStopLines = buildStopLines(stopOrders)
  const protection = resolveWorkspaceProtection({
    isActive: effectiveStage === 'active',
    symbol: symbolUpper,
    ordersReady,
    stopOrders,
    brokerPositions,
    brokerPositionsReady: positionsReady,
    openTrades,
  })

  return {
    symbol: symbolUpper,
    storedStage,
    effectiveStage,
    sourceReason,
    isIdea: effectiveStage === 'idea',
    isWorking: effectiveStage === 'working',
    isActive: effectiveStage === 'active',
    isClosed,
    hasSavedSetup: hasSavedSetup(primary),
    hasLiveBrokerOrders: liveBrokerOrders.length > 0,
    hasOpenBrokerPosition: openBrokerPositions.length > 0,
    brokerOrdersReady: Boolean(ordersReady),
    brokerPositionsReady: Boolean(positionsReady),
    protection,
    liveStopLines,
    liveStopPrice: liveStopLines[0]?.price ?? null,
    brokerEvidence: {
      liveOrders: liveBrokerOrders,
      openPositions: openBrokerPositions,
    },
    accountNumbers: [...accounts],
  }
}
